import React, { useContext, useState, useRef } from "react";
import Map, { Marker, NavigationControl } from "react-map-gl";
import ActiveLocationContext from "hooks/active-location-context";
import InfoBoxContext from "hooks/info-box-context";
import { useMapWithViewport } from "hooks/useMapWithViewport";
import { useSize } from "react-hook-size";

import CloseButton from "assets/svgs/close-button.svg";
import CustomMarker from "assets/svgs/map-marker.svg";

import "mapbox-gl/dist/mapbox-gl.css";

const mapProps = {
  latitude: 39.82,
  longitude: -98.35,
  zoom: 4,
};

const maxZoom = 20;

const EventMarker = ({
  selected,
  altPinSelected,
  latitude,
  longitude,
  onClick,
}) => {
  const baseClassNames =
    "transition origin-bottom duration-400 ease-out transform relative w-6 h-6 cursor-pointer z-10 -left-3 -top-6";

  return (
    <Marker
      latitude={latitude}
      longitude={longitude}
      anchor="bottom"
      onClick={onClick}
    >
      <div
        className={`${baseClassNames} ${
          selected ? "transform scale-125 text-red" : ""
        } ${altPinSelected ? "transform scale-125" : ""}`}
      >
        <CustomMarker className="w-8" />
      </div>
    </Marker>
  );
};

export default function EventMap({
  activeTimelineEventLocationId,
  fullLocations,
}) {
  const mapRef = useRef();
  const mapContainerRef = useRef();

  const { setActiveLocationId, activeLocationId } = useContext(
    ActiveLocationContext
  );

  const { showInfoBox, setShowInfoBox } = useContext(InfoBoxContext);

  // State for the secondary pin selection
  const [altSelected, setAltSelected] = useState(false);

  const markers = fullLocations?.map((place) => {
    const latLng = place.coordinates ? place.coordinates.split(",") : [];
    const latitude = parseFloat(latLng[0]);
    const longitude = parseFloat(latLng[1]);

    return {
      place_id: place.item_id,
      latitude,
      longitude,
    };
  });

  const handleMarkerClick = (place_id) => {
    setShowInfoBox(true);
    setActiveLocationId(place_id);
    setAltSelected(true);
  };

  const { width, height } = useSize(mapContainerRef);

  const { viewport, onViewportChange } = useMapWithViewport({
    width: width || 400,
    height: height || 400,
    markers,
  });

  const EventInfoBox = ({ fullLocations }) => {
    let activeLocation = fullLocations[0];

    if (activeLocationId) {
      let filteredLocations = fullLocations.filter(
        (fullLocation) => fullLocation.item_id === activeLocationId
      );
      activeLocation = filteredLocations[0];
    }

    const handleCloseButtonClick = () => {
      setShowInfoBox(false);
      setAltSelected(false);
    };

    return (
      <article
        id="EventInfoBox"
        className={`absolute z-100 bg-white bottom-4 duration-300 left-4 p-4 right-16 transition-opacity ${
          !showInfoBox ? "opacity-0 pointer-events-none" : ""
        }`}
      >
        <div className="flex justify-between">
          <div>
            {activeLocation && (
              <h3 className="font-semibold">{activeLocation.summary_text}</h3>
            )}
            {activeLocation && (
              <p className="text-gray-500 text-xs">
                {activeLocation.city && activeLocation.city}
                {activeLocation.state && <>, {activeLocation.state}</>}
              </p>
            )}
          </div>
          <CloseButton
            className="h-4 w-4"
            onClick={() => handleCloseButtonClick()}
          />
        </div>
        {activeLocation && (
          <p className="mt-4 text-xs">{activeLocation.description_text}</p>
        )}
      </article>
    );
  };

  return (
    <div
      className="relative h-96 mx-auto w-full lg:h-full"
      ref={mapContainerRef}
    >
      <Map
        initialViewState={mapProps}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_API_TOKEN}
        ref={mapRef}
        maxZoom={maxZoom}
        {...viewport}
        onMove={(e) => {
          onViewportChange(e.viewState);
        }}
      >
        <NavigationControl className="navigation-control" showCompass={false} />
        {markers?.map((marker, index) => {
          const { latitude, longitude } = marker;
          if (isNaN(latitude) || isNaN(latitude)) {
            return null;
          }

          return (
            <EventMarker
              altPinSelected={
                altSelected && marker.place_id === activeLocationId
              }
              key={index}
              latitude={latitude}
              longitude={longitude}
              selected={marker.place_id === activeTimelineEventLocationId}
              onClick={() => {
                handleMarkerClick(marker.place_id);
              }}
            />
          );
        })}
      </Map>

      <EventInfoBox fullLocations={fullLocations} />
    </div>
  );
}
